import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import ProfileSection from "components/Screens/Marketplace/Customer/ProfileSection/ProfileSection";
import TopHeader from "components/common/TopHeader";

import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";
import type { TalentProfile } from "components/Screens/Marketplace/Customer/types";
import getTalentFullName from "components/Screens/Marketplace/Customer/utils/talentName";
import PageLoader from "components/common/PageLoader";
import ViewLimitWall from "components/Screens/Marketplace/Customer/ProfilePage/ViewLimitWall";
import { ViewLimitError } from "components/Screens/Marketplace/Customer/common/viewLimit";
import TalentNetworkPage from "components/Screens/Marketplace/Customer/TalentNetwork/TalentNetworkPage";
import { useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import { trackPlg } from "plg/analytics/events";
import type { CapType } from "plg/analytics/events";
import { profileViewCount } from "plg/analytics/plgAnalyticsSession";

import { setBreadcrumbLabel } from "store/actions/Breadcrumbs";

import "components/Screens/Marketplace/Customer/ProfilePage/ProfilePage.scss";

/** The portal's 429 codes as the tracking sheet names the caps. */
const CAP_TYPES: Record<string, CapType> = {
  VIEW_LIMIT_SESSION: "session",
  VIEW_LIMIT_DAILY: "user_daily",
  VIEW_LIMIT_ACCOUNT: "account_daily",
};

function ProfilePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [talentData, setTalentData] = useState<TalentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewLimit, setViewLimit] = useState<string | null>(null);
  const didFetchRef = useRef(false);
  const api = useTalentApi();
  const { count: shortlistCount, loading: shortlistLoading } = useShortlist();
  const [capHit, setCapHit] = useState<CapType | null>(null);
  const userAdditionalInfoLoading = api.userInfoLoading;

  useEffect(() => {
    const load = async () => {
      if (!id || didFetchRef.current) return;
      didFetchRef.current = true;
      setError(null);
      try {
        const data = await api.fetchTalentProfile(id);
        setTalentData(data);
        const name = getTalentFullName(data);
        dispatch(setBreadcrumbLabel(location.pathname, name));
      } catch (e) {
        setTalentData(null);
        if (e instanceof ViewLimitError) {
          setViewLimit(e.message);
          if (api.kind === "plg" && CAP_TYPES[e.code]) setCapHit(CAP_TYPES[e.code]);
        }
        else setError(e instanceof Error ? e.message : "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Sent once the shortlist has loaded, so shortlist_count is the real one.
  useEffect(() => {
    if (!capHit || shortlistLoading) return;
    trackPlg("cap_wall_hit", { cap_type: capHit, shortlist_count: shortlistCount, views_in_window: profileViewCount() });
    setCapHit(null);
  }, [capHit, shortlistLoading, shortlistCount]);

  const title = getTalentFullName(talentData);

  if (loading || userAdditionalInfoLoading) {
    return <PageLoader />;
  }

  // PLG: the Browse list right here, blurred behind the wall; the close button goes to Browse.
  if (viewLimit && api.kind === "plg") {
    return (
      <>
        <TalentNetworkPage />
        <ViewLimitWall
          overlay
          message={viewLimit}
          requestAHireRoute={api.routes.requestAHire}
          onClose={() => navigate(api.routes.talentNetwork)}
        />
      </>
    );
  }

  if (viewLimit) {
    return <ViewLimitWall message={viewLimit} requestAHireRoute={api.routes.requestAHire} closeRoute={api.routes.talentNetwork} />;
  }

  if (error || !talentData) {
    return (
      <div className="profile-content">
        <div className="error-message">Error loading profile: {error || "Profile not found"}</div>
      </div>
    );
  }

  return (
    <>
      <TopHeader
        title={title}
        breadcrumbs={false}
        // Back to Browse (the list comes back as it was left: filters, page, scroll); PLG and portal have their own route
        backIcon={api.routes.talentNetwork}
        primaryBtn={undefined}
        secondaryBtn={undefined}
        subtext={undefined}
        primaryBtnClick={undefined}
        secondaryBtnClick={undefined}
      />
      <div className="profile-page">
        <div className="profile-page__container">
          <div className="profile-page__profile">
            <ProfileSection profileId={id} initialTalent={talentData} />
          </div>
        </div>
      </div>
    </>
  );
}

export default ProfilePage;
